import { Button, Col, Form, Input, Row } from "antd";
import { useAppLoading } from "contexts";
import shumokuApi from "features/shumoku/shumokuApi";
import { shumokuActions } from "features/shumoku/shumokuSlice";
import {
  ECreateMajorItemMasterError,
  TCreateMajorItemMasterReq,
} from "model/shumoku";
import { TRANSLATION } from "pages/MajorItemManagement/MajorItemManagement.translate";
import { FC, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TResError, TResponse } from "types/service";
import { MajorItemCreateFormModalStyled } from "./MajorItemCreateFormModal.styled";
import { ValidateErrorEntity } from "types/antd";

const initialValues = {
  majorItemCode: "",
  majorItemName: "",
};

const CREATE_SHUMOKU_FIELD = {
  MAJOR_ITEM_CODE: {
    LABEL: TRANSLATION.MAJOR_ITEM_CODE,
    NAME: "majorItemCode",
  },
  MAJOR_ITEM_NAME: {
    LABEL: TRANSLATION.MAJOR_ITEM_NAME,
    NAME: "majorItemName",
  },
};

type TProps = {
  handleOk: () => void;
};

const MajorItemCreateFormModal: FC<TProps> = ({ handleOk }) => {
  const [form] = Form.useForm();
  const { startLoading, stopLoading } = useAppLoading();
  const dispatch = useDispatch();

  const setErrorForField = ({
    fieldName,
    message = "",
  }: {
    fieldName: string;
    message?: string;
  }) => {
    form.getFieldInstance(fieldName).focus();
    form.setFields([{ name: fieldName, errors: [message] }]);
  };
  const showErrors = useCallback((errorCode: string) => {
    switch (errorCode) {
      case ECreateMajorItemMasterError.MAJOR_ITEM_CODE_IS_EXISTED:
        setErrorForField({
          fieldName: CREATE_SHUMOKU_FIELD.MAJOR_ITEM_CODE.NAME,
          message: TRANSLATION.MAJOR_ITEM_CODE_EXISTED,
        });
        break;
      case ECreateMajorItemMasterError.MAJOR_ITEM_CODE_IS_INVALID:
        setErrorForField({
          fieldName: CREATE_SHUMOKU_FIELD.MAJOR_ITEM_CODE.NAME,
          message: TRANSLATION.MAJOR_ITEM_CODE_INVALID,
        });
        break;
      case ECreateMajorItemMasterError.MAJOR_ITEM_NAME_IS_EXISTED:
        setErrorForField({
          fieldName: CREATE_SHUMOKU_FIELD.MAJOR_ITEM_NAME.NAME,
          message: TRANSLATION.MAJOR_ITEM_NAME_EXISTED,
        });
        break;

      default:
        setErrorForField({
          fieldName: "",
          message: "",
        });
        break;
    }
  }, []);

  const handleCreateError = (errors: TResError[]) => {
    errors.forEach((element: TResError) => {
      showErrors(element.code);
    });
  };
  const onFinish = async (values: any) => {
    startLoading();
    try {
      const { data: createInfo } = await shumokuApi.createMajorItemMaster(
        values
      );
      if (createInfo) {
        dispatch(shumokuActions.getAllShumokus());
        handleOk();
        form.resetFields();
      }
    } catch (error: any) {
      const errorList = (error?.data as TResponse)?.errors || [];
      if (errorList) {
        handleCreateError(errorList);
      }
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    form
      .getFieldInstance(CREATE_SHUMOKU_FIELD.MAJOR_ITEM_CODE.NAME || "")
      ?.focus();
  }, []);

  const handleFinishFailed = (
    errorInfo: ValidateErrorEntity<TCreateMajorItemMasterReq>
  ) => {
    const firstFiledError = errorInfo.errorFields[0].name[0];
    form.getFieldInstance(firstFiledError)?.focus();
  };

  const trimFieldValue = (filedName: string) => {
    const fieldValue = form.getFieldValue(filedName);
    form.setFieldValue(filedName, fieldValue?.trim());
  };

  return (
    <MajorItemCreateFormModalStyled>
      <Form
        initialValues={initialValues}
        layout="vertical"
        style={{ maxWidth: 600 }}
        onFinish={onFinish}
        form={form}
        onFinishFailed={handleFinishFailed}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name={CREATE_SHUMOKU_FIELD.MAJOR_ITEM_CODE.NAME}
              label={CREATE_SHUMOKU_FIELD.MAJOR_ITEM_CODE.LABEL}
              labelAlign="right"
              rules={[
                {
                  required: true,
                  transform: (value) => value.trim(),
                  message: `${TRANSLATION.MAJOR_ITEM_CODE_REQUIRED}`,
                },
                {
                  max: 50,
                  message: `${TRANSLATION.MAJOR_ITEM_CODE_MAX_LENGTH}`,
                },
              ]}
            >
              <Input
                onBlur={() => {
                  trimFieldValue(CREATE_SHUMOKU_FIELD.MAJOR_ITEM_CODE.NAME);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name={CREATE_SHUMOKU_FIELD.MAJOR_ITEM_NAME.NAME}
              label={CREATE_SHUMOKU_FIELD.MAJOR_ITEM_NAME.LABEL}
              rules={[
                {
                  required: true,
                  transform: (value) => value.trim(),
                  message: `${TRANSLATION.MAJOR_ITEM_NAME_REQUIRED}`,
                },
              ]}
            >
              <Input
                onBlur={() => {
                  trimFieldValue(CREATE_SHUMOKU_FIELD.MAJOR_ITEM_NAME.NAME);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" align="bottom">
          <Form.Item style={{ marginBottom: "0px" }}>
            <Button type="primary" htmlType="submit">
              {/* !Add space end of text => to fix issue `auto add space between 2 chars in button` of antd */}
              {TRANSLATION.SAVE}
              {""}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    </MajorItemCreateFormModalStyled>
  );
};
export { MajorItemCreateFormModal };
